import * as React from 'react'

import { VehicleModel } from '@/app/models'
import useVehicleService from '../services/VehicleService'
import VehicleBasicInfo from '../tabs/list/components/VehicleBasicInfo'
import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useVehicles } from '@/modules/shared/context/vehicles/VehiclesContext'
import { VehicleFormProvider } from '../context/VehicleFormContext'
import AddVehicleModal from '../modals/vehicleAdd/AddVehicleModal'
import { useQueryClient } from '@tanstack/react-query'
import { QUERY_KEY_CONFIG_COUNTRIES } from '@/lib/api/constants'
import { RouteComponentProps } from '@/app/navigation/withRouter'

interface RouteParams {
  companyId: string
  vehicleId: string
}

export const VehicleBasicDataPage: React.FC<
  RouteComponentProps<RouteParams>
> = ({ match, history }) => {
  const { params } = match
  const { vehicleId, companyId } = params
  const { setSelectedVehicleId, refresh } = useVehicles()
  const queryClient = useQueryClient()
  const [vehicle, setVehicle] = React.useState<VehicleModel | null>(null)
  const { getVehicle } = useVehicleService()
  const loading = useLoadingStore(selectLoading)
  const [isOpenEditVehicle, setIsOpenEditVehicle] = React.useState(false)

  const fetchVehicle = async () => {
    try {
      const data = await getVehicle({
        companyId: companyId,
        vehicleId: vehicleId,
      })
      setVehicle(data)
    } catch (err) {
      console.error('Error fetching vehicle:', err)
      setVehicle(null)
    }
  }

  React.useEffect(() => {
    if (vehicleId) {
      setSelectedVehicleId(vehicleId.toString())
      fetchVehicle()
    }
  }, [vehicleId, companyId])

  return (
    <section className="tw-h-full tw-min-h-fit tw-overflow-y-auto tw-px-5 tw-pb-4">
      {loading ? (
        <div className="tw-grid tw-h-full tw-place-items-center">
          <LoaderIcon className="tw-w-16 tw-animate-spin" />
        </div>
      ) : (
        <>
          {vehicle ? (
            <VehicleBasicInfo
              vehicle={vehicle}
              setIsOpenEditVehicle={setIsOpenEditVehicle}
              companyId={companyId}
              vehicleId={vehicleId}
              onRefresh={() => {
                fetchVehicle()
                // Bez tego lista po lewej zostaje z poprzednim statusem, a filtr
                // Aktywne/Nieaktywne przestaje się zgadzać z tym, co widać na ekranie.
                refresh()
                queryClient.invalidateQueries({ queryKey: [QUERY_KEY_CONFIG_COUNTRIES, companyId] })
              }}
            />
          ) : (
            <div>Vehicle not found</div>
          )}
        </>
      )}
      {isOpenEditVehicle && (
        <>
          <VehicleFormProvider
            companyId={companyId}
            vehicleId={vehicleId}
            isEdit={true}
          >
            <AddVehicleModal
              isOpen={isOpenEditVehicle}
              refreshVehicle={fetchVehicle}
              close={() => setIsOpenEditVehicle(false)}
              companyId={companyId}
            />
          </VehicleFormProvider>
        </>
      )}
    </section>
  )
}
